import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import store from '@Store';
import { parse } from 'query-string';
import { generateTraceHeaders } from '@Services/CorrelationId';
import { LLS_RESPONSE_LOCATION_VALUE, elsevierExternalUrlStrings } from '@Components/Results/constants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import RsModal from '@Pages/RetroSynthesis/components/RsModal';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import FullTextDialogComponent from '@Components/Results/reactions/reaction-details/full-text-dialog.component';
import FullTextContentComponent from '@Components/Results/reactions/reaction-details/full-text-content.component';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import {
  setFullTextDocumentId as hopintoSetFullTextDocumentId,
  getFullTextDocumentId as hopintoGetFullTextDocumentId,
} from '@Containers/Hopinto/Services/FullTextResults';
import { getAnalyticsForFullTextLink } from '@Components/Results/analytics/fullTextAnalytics';
import { getLinkIndex } from '@Components/Results/analytics/citationLinksTracking';

import './full-text.component.custom.scss';

function FullTextComponent({ citation, trackingAttributes, metadata }) {
  const [isModalShown, toggleModalShown] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [links, setLinks] = React.useState(null);
  const [fullTextIndex, setFullTextIndex] = React.useState('0');
  const fullTextButtonRef = useRef();

  React.useEffect(() => {
    const hopintoFullTextDocumentId = hopintoGetFullTextDocumentId();
    if (hopintoFullTextDocumentId && hopintoFullTextDocumentId === citation.id) {
      // eslint-disable-next-line no-use-before-define
      handleLinkClick();
      hopintoSetFullTextDocumentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line no-shadow
  const handleResult = (result, fullTextIndex) => {
    getAnalyticsForFullTextLink({
      trackingAttributes,
      citation,
      linksObject: result,
      fullTextIndex,
      metadata,
      buttonElement: fullTextButtonRef.current,
    });
    if (result) {
      const linksKeys = Object.keys(result);
      if (linksKeys.length === 1) {
        const currentLinkKey = linksKeys[0];
        window.open(
          result[currentLinkKey] &&
            window.s &&
            window.s.visitor &&
            elsevierExternalUrlStrings.some((substring) => result[currentLinkKey].includes(substring))
            ? window.s.visitor.appendVisitorIDsTo(result[currentLinkKey])
            : result[currentLinkKey],
          '_blank'
        );
        return;
      }
    }
    toggleModalShown(true);
  };

  const getLinksFromLLSResponse = ({ valid, responses } = {}) => {
    if (!valid) {
      return {};
    }

    if (!isUndefined(responses[LLS_RESPONSE_LOCATION_VALUE])) {
      return { [LLS_RESPONSE_LOCATION_VALUE]: responses[LLS_RESPONSE_LOCATION_VALUE] };
    }

    return responses;
  };

  const handleLinkClick = async (event) => {
    // eslint-disable-next-line no-shadow
    const fullTextIndex = getLinkIndex(event && event.currentTarget);
    setFullTextIndex(fullTextIndex);
    if (isSending) {
      return;
    }

    if (links) {
      handleResult(links, fullTextIndex);
      return;
    }

    setIsSending(true);
    try {
      // The problem with using MessageBroker and axios: browser blocks popup (window.open) after axios request for some reason...
      const response = await fetch(citation.fullTextUrl, {
        method: 'GET',
        headers: {
          ...generateTraceHeaders(),
        },
      });
      const data = await response.json();
      // eslint-disable-next-line no-shadow
      const links = getLinksFromLLSResponse(data);
      setLinks(links);
      handleResult(links, fullTextIndex);
    } catch (error) {
      Logger.error(error);
      handleResult(links, fullTextIndex);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => toggleModalShown(false);

  const handleSuccess = () => {
    store.dispatch(showCopiedToastMessage());
  };

  if (!citation.fullTextUrl) {
    return null;
  }

  const searchQuery = citation.fullTextUrl.split('?')[1];
  const params = parse(searchQuery);

  const type = get(citation, 'type', '');
  return (
    <div className="full-text">
      <RsModal showBackdrop showModal={isModalShown} className="full-text-dialogs-root">
        <FullTextDialogComponent
          onClose={handleClose}
          onCopy={handleSuccess}
          isPatent={type.toLowerCase().includes('patent')}
          fields={params}
          links={links}
          citation={citation}
          trackingAttributes={trackingAttributes}
          fullTextIndex={+fullTextIndex}
          metadata={metadata}
          buttonElement={fullTextButtonRef.current}
        />
      </RsModal>

      <button
        type="button"
        onClick={handleLinkClick}
        className="els-btn els-btn-link els-btn-xs blue-compact e2e-full-text"
        ref={fullTextButtonRef}>
        {isSending ? <SimpleSpinner /> : <FullTextContentComponent fullTextIconUrl={citation.fullTextIconUrl} />}
      </button>
    </div>
  );
}

FullTextComponent.propTypes = {
  citation: PropTypes.object.isRequired,
  trackingAttributes: PropTypes.object,
  metadata: MetadataPropTypesShape,
};

export default FullTextComponent;
